import { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RefreshCw, History } from 'lucide-react';

export function HierarchySyncPanel({ onSynced }) {
  const [changes, setChanges] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchChanges = useCallback(async () => {
    setLoading(true);
    try {
      const [changesRes, gapsRes] = await Promise.all([
        userAPI.hierarchyChanges({ limit: 20 }),
        userAPI.hierarchyGaps(),
      ]);
      setChanges(changesRes.data.changes || []);
      setGaps(gapsRes.data.gaps || []);
    } catch {
      toast.error('Failed to load recent hierarchy changes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChanges(); }, [fetchChanges]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await userAPI.syncHierarchyFromCRM();
      const a = res.data.applied || {};
      toast.success(`Synced: ${a.updated || 0} updated, ${a.gaps || 0} gap(s), ${a.loops || 0} loop(s) flagged`);
      await fetchChanges();
      onSynced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="hierarchy-sync-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Zoho CRM is the only source for Reports To for users with a CRM match. Staff
            with no CRM record (set manually on the Users page) keep their line until CRM
            picks them up. Runs automatically every day at 04:00 UTC.
          </p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto rounded-sm" disabled={syncing} onClick={runSync} data-testid="sync-hierarchy-from-crm-btn">
          {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Sync hierarchy from CRM now
        </Button>
      </div>

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <div className="px-4 pt-3 pb-1 text-xs font-medium uppercase text-muted-foreground">Recent hierarchy changes</div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium text-xs uppercase">Person</TableHead>
                  <TableHead className="font-medium text-xs uppercase">Old Manager</TableHead>
                  <TableHead className="font-medium text-xs uppercase">New Manager</TableHead>
                  <TableHead className="font-medium text-xs uppercase">Source</TableHead>
                  <TableHead className="font-medium text-xs uppercase">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changes.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No hierarchy changes yet</TableCell></TableRow>
                ) : changes.map((c) => (
                  <TableRow key={c.id} data-testid={`hierarchy-change-${c.id}`}>
                    <TableCell className="font-medium text-sm">
                      {c.user_name}
                      <span className="block text-xs text-muted-foreground">{c.user_email}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.old_manager_name || '— Top Level'}</TableCell>
                    <TableCell className="text-sm">
                      {c.new_manager_name || '— Top Level'}
                      {c.gap_reason && (
                        <Badge variant="outline" className="ml-2 rounded-sm text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200">
                          Gap
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-xs font-normal">{c.source}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(c.timestamp).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm" data-testid="hierarchy-gaps-panel">
        <CardContent className="p-0">
          <div className="px-4 pt-3 pb-1 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-medium uppercase text-muted-foreground">
              Hierarchy gaps {gaps.length > 0 && `(${gaps.length})`}
            </span>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : gaps.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">No hierarchy gaps right now</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium text-xs uppercase">Person</TableHead>
                  <TableHead className="font-medium text-xs uppercase">Current Manager</TableHead>
                  <TableHead className="font-medium text-xs uppercase">Why it's a gap</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gaps.map((g) => (
                  <TableRow key={g.id} data-testid={`hierarchy-gap-${g.id}`}>
                    <TableCell className="font-medium text-sm">
                      {g.name}
                      <span className="block text-xs text-muted-foreground">{g.email}</span>
                    </TableCell>
                    <TableCell className="text-sm">{g.reporting_to_name || '— Top Level'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{g.hierarchy_gap_reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
